<template>
  <l-map
    ref="map"
    :center="center"
    :options="options"
    style="height: 100%; width: 100%"
    @popupclose="popupClosed"
    @ready="afterMapReady"
  >
    <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
    <l-control-zoom position="topright" />

    <StoreLocatorMarker
      v-for="store in stores"
      :key="store.id"
      ref="markers"
      :position="position"
      :store="store"
      @click="setCurrentStore(store)"
    />
  </l-map>
</template>

<script>
import StoreLocatorMarker from './StoreLocatorMarker.vue';

export default {
  name: 'StoreLocatorMap',
  components: { StoreLocatorMarker },
  props: {
    position: {
      type: Object,
      default: null,
    },
    offsetX: {
      type: Number,
      default: 0,
    },
    selectedStore: {
      type: Object,
      default: null,
    },
    stores: {
      type: Array,
      required: true,
    },
    defaultMapPosition: {
      type: Object,
      required: true,
    },
    defaultMapBounds: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      map: null,
      center: {
        lat: this.defaultMapPosition.latitude,
        lng: this.defaultMapPosition.longitude,
      },
      currentStore: null,
    };
  },
  computed: {
    /**
     * @return {Object}
     */
    options() {
      return {
        attributionControl: false,
        zoomControl: false,
      };
    },
  },
  watch: {
    /**
     * @param {Object} value
     */
    selectedStore: function (value) {
      this.setCurrentStore(value);
    },
    /**
     * @param {Object} value
     */
    position: function (value) {
      this.setCenter(value);
    },
  },
  methods: {
    afterMapReady() {
      this.map = this.$refs.map.mapObject;

      this.zoomMap();
    },
    zoomMap() {
      // get maximum zoom
      let zoom = this.map.getBoundsZoom(this.defaultMapBounds);

      // get left (which is the center of the map) and right bounds according to this zoom value
      const center = this.map.project(this.map.getCenter(), zoom);
      const leftBound = this.map.unproject(center, zoom);
      const rightBound = this.map.unproject(
        { x: center.x + this.map.getSize().x / 2, y: center.y },
        zoom
      );

      // if France doesn't fully fit with this zoom value then decrement
      if (
        rightBound.lng - leftBound.lng <
        this.defaultMapBounds[1][1] - this.defaultMapBounds[3][1]
      ) {
        --zoom;
      }

      this.map.setZoom(zoom);

      this.setCenter({
        latitude: this.center.lat,
        longitude: this.center.lng,
      });
    },
    popupClosed() {
      this.$refs.markers.forEach((marker) => marker.popupClosed());
    },
    /**
     * @param {{latitude: number, longitude: number}} latLng
     * @param {number} offsetX
     * @param {number} offsetY
     * @return {{lng: number, lat: number}}
     * @see https://stackoverflow.com/a/10666030/1224564
     */
    offsetCenter({ latitude, longitude }, offsetX, offsetY) {
      // latLng is the apparent centre-point
      // offsetX is the distance you want that point to move to the right, in pixels
      // offsetY is the distance you want that point to move upwards, in pixels
      // offset can be negative
      // offsetX and offsetY are both optional

      const worldCoordinateCenter = this.map.project({
        lat: latitude,
        lng: longitude,
      });
      const pixelOffset = L.point(offsetX || 0, offsetY || 0);
      const worldCoordinateNewCenter = L.point(
        worldCoordinateCenter.x - pixelOffset.x,
        worldCoordinateCenter.y + pixelOffset.y
      );

      return this.map.unproject(worldCoordinateNewCenter);
    },
    /**
     * @param {{ latitude, longitude }} center
     */
    setCenter(center) {
      this.center = this.offsetCenter(center, this.offsetX, 0);
    },
    /**
     * @param {Object} store
     */
    setCurrentStore(store) {
      this.setCenter(store);
      this.currentStore = store;
      this.$emit('update:store', store);
    },
  },
};
</script>
